import { parseISO } from 'date-fns'
import { isEqual, pick, sortBy } from 'lodash'
import * as React from 'react'
import { connect, ConnectedComponent } from 'react-redux'
import { actions } from 'react-redux-form'

import { getAirports, mapAPIToAirport } from '../../../data/api/airports'
import {
  refreshCurrentTotal,
  refreshDiscountCode,
} from '../../../data/api/order'
import { fetchPrices, IPricesResult } from '../../../data/api/prices'
import Airport from '../../../data/models/airport'
import { FunnelOrder } from '../../../data/models/funnelOrder'
import {
  CountryAirports,
  getVisibleAirports,
} from '../../../data/models/funnelOrder/airport'
import {
  driveThemes,
  flyThemes,
  Theme,
} from '../../../data/models/funnelOrder/theme'
import { updateFunnelOrder } from '../../../data/models/funnelOrder/utils'
import {
  getSiteCurrencyISO,
  SiteCountry,
  SiteCurrency,
} from '../../../data/models/locale'
import { AppState } from '../../../data/state'
import history from '../../../routing/history'
import { productDetailPage } from '../../../tracking/actions/productDetailPage'
import { format } from '../../../utils/date'
import { createQueryString, getQueryParameters } from '../../../utils/urls'
import { getOptions } from './utils/getOptions'
import { QueryKey } from './wrapper'

export interface CustomiseWrapperInnerProps {
  loading: boolean
  prices: MonthData[]
  airports: Airport[]
}

export interface MonthData {
  pp: number
  bookings?: number
  max_bookings?: number
  discount_pp?: number
}

interface WrapperProps {
  funnelOrder: FunnelOrder
  currency: SiteCurrency
  country: SiteCountry
  departureDate: Date | null
  theme: Theme
  addAirports(airports: Airport[]): void
  setAirports(aiports: CountryAirports[]): void
  setDay(day: Date | null): void
  setFunnelOrder(value: FunnelOrder): void
  setMonthAndYear(month: number, year: number): void
}

interface WrapperState {
  initLoading: boolean
  loading: boolean
  hasMonth: boolean
  order: FunnelOrder
  prices: MonthData[]
  airports: Airport[]
}

export function withAirportsAndPrices<T>(
  WrappedComponent:
    | React.ComponentClass<CustomiseWrapperInnerProps & any>
    | ConnectedComponent<CustomiseWrapperInnerProps & any, any>
    | React.FC<CustomiseWrapperInnerProps & any>,
  queryKeys: QueryKey[],
  forceMonth?: boolean,
  skipAirports?: boolean,
) {
  class Wrapper extends React.Component<WrapperProps & T, WrapperState> {
    private query = getQueryParameters()

    constructor(props: WrapperProps & T) {
      super(props)
      this.state = {
        initLoading: true,
        loading: true,
        order: this.props.funnelOrder,
        hasMonth: false,
        prices: [],
        airports: [],
      }
    }

    public async componentDidMount() {
      if (!skipAirports) {
        await this.fetchAirports()
      }
      await this.getOptionsFromUrl()
      this.setUrlOptions()
      await this.fetchPrices(true)
      await this.fetchCurrentTotal()
      await this.updateDiscountCode()
    }

    public shouldComponentUpdate(
      nextProps: Readonly<WrapperProps & T>,
      nextState: Readonly<WrapperState>,
      nextContext: any,
    ): boolean {
      const { funnelOrder, country, theme } = nextProps
      const { initLoading } = nextState
      const airports: Airport[] =
        new Map(funnelOrder.airports).get(country) || []
      if (flyThemes.includes(theme) && airports.length === 0) {
        return true
      }
      return !initLoading
    }

    public pricesShouldUpdate(oldProps: WrapperProps) {
      if (this.state.loading) {
        return false
      }

      const keys = [
        'year',
        'month',
        'destination',
        'theme',
        'days',
        'numberOfAdults',
        'numberOfChildren',
        'numberOfBabies',
        'roomType',
        'locations',
        'airports',
        'incomingTime',
        'outgoingTime',
        'excludedCitiesIds',
        'includedCitiesIds',
      ]

      return (
        !isEqual(
          pick(oldProps.funnelOrder, keys),
          pick(this.props.funnelOrder, keys),
        ) ||
        oldProps.country !== this.props.country ||
        oldProps.currency !== this.props.currency
      )
    }

    public discountCodeShouldUpdate(oldProps: WrapperProps) {
      return (
        oldProps.country !== this.props.country ||
        oldProps.currency !== this.props.currency
      )
    }

    public updateDiscountCode = () => {
      const { currency, funnelOrder } = this.props
      if (funnelOrder.discountCode) {
        refreshDiscountCode(
          this.props.country,
          getSiteCurrencyISO(currency),
          funnelOrder.discountCode.code,
        )
      }
    }

    public updatePrices = () => {
      this.fetchPrices().then(() => {
        this.fetchCurrentTotal()
      })
    }

    public async componentDidUpdate(oldProps: WrapperProps) {
      if (!isEqual(this.props.funnelOrder, this.state.order)) {
        this.setState({ order: this.props.funnelOrder })
      }
      if (this.props.departureDate !== oldProps.departureDate) {
        await this.fetchCurrentTotal()
      }
      this.setUrlOptions()
      if (this.pricesShouldUpdate(oldProps)) {
        if (oldProps.country !== this.props.country && !skipAirports) {
          await this.fetchAirports()
        }
        await this.updatePrices()
      }
      if (this.discountCodeShouldUpdate(oldProps)) {
        this.updateDiscountCode()
      }
    }

    public render() {
      return (
        <WrappedComponent
          prices={this.state.prices}
          airports={this.state.airports}
          loading={this.state.loading}
          {...this.props}
        />
      )
    }

    private fetchAirports = () => {
      return new Promise<void>((resolve, reject) => {
        const { theme, country } = this.props
        if (!driveThemes.includes(theme)) {
          getAirports(country, theme)
            .then(airports => {
              this.setState({ airports }, resolve)
            })
            .catch(reject)
        } else {
          resolve()
        }
      })
    }

    private fetchCurrentTotal = () => {
      if (this.props.departureDate) {
        refreshCurrentTotal().then(() => productDetailPage())
      }
    }

    private fetchPrices = (initLoad: boolean = false) => {
      return new Promise<void>((resolve, reject) => {
        this.setState({ loading: true }, () => {
          const fromDate = this.getFromDate(initLoad)
          fetchPrices(
            this.state.order,
            this.props.currency,
            this.props.country,
            fromDate,
          )
            .then(data => {
              const newDate = this.updateFirstMonth(data)
              if (!skipAirports) {
                this.updateAiport(data)
              }
              const prices = this.getPricesFromData(data, newDate)
              this.setDate(prices)
              this.setState({ initLoading: false }, () => {
                this.setState(
                  {
                    prices,
                    loading: false,
                  },
                  resolve,
                )
              })
            })
            .catch(reject)
        })
      })
    }

    private setDayNullIfNoPrice = (prices: MonthData[]) => {
      const date =
        this.props.departureDate !== null
          ? new Date(this.props.departureDate as Date)
          : null
      if (date !== null) {
        const dayData = prices[date.getDate()]
        if (dayData === undefined) {
          if (
            date.getMonth() + 1 === this.props.funnelOrder.month &&
            date.getFullYear() === this.props.funnelOrder.year
          ) {
            // no price
            return null
          }
        } else if (
          dayData.bookings !== undefined &&
          dayData.max_bookings !== undefined
        ) {
          if (dayData.bookings >= dayData.max_bookings) {
            // fully booked
            return null
          }
        }
      }
      return date
    }

    private setDate = (prices: MonthData[]) => {
      const date = this.setDayNullIfNoPrice(prices)
      if (date === null) {
        const availablePrices = prices.filter(
          x =>
            x.bookings === undefined ||
            x.max_bookings === undefined ||
            x.bookings < x.max_bookings,
        )

        const cheapest = sortBy(availablePrices.map(x => x.pp))[0]
        if (cheapest) {
          const cheapestData = availablePrices.filter(x => x.pp === cheapest)[0]
          if (cheapestData) {
            const day = prices.indexOf(cheapestData)
            const { month, year } = this.props.funnelOrder
            if (day && month && year) {
              this.props.setDay(new Date(year, month - 1, day))
              return
            }
          }
        }
        this.props.setDay(null)
      }
    }

    private getFromDate = (initLoad: boolean) => {
      const getDate = () => {
        const { month, year } = this.props.funnelOrder
        if (month && year) {
          return new Date(year, month - 1, 1)
        }
      }

      if (
        !initLoad ||
        this.state.hasMonth ||
        forceMonth ||
        this.props.departureDate !== null
      ) {
        return getDate()
      }
      return undefined
    }

    private getPricesFromData = (data: IPricesResult, newDate?: Date) => {
      const { year, month } = this.props.funnelOrder
      if (year && month) {
        const dateStart = newDate
          ? format(newDate, 'yyyy-MM')
          : format(new Date(year, month - 1, 1), 'yyyy-MM')
        const dates: MonthData[] = []
        for (const item of data.prices) {
          if (item.date.startsWith(dateStart)) {
            const itemDate = parseISO(item.date)
            dates[itemDate.getDate()] = {
              pp: item.pp,
              bookings: item.bookings,
              max_bookings: item.max_bookings,
              discount_pp: item.discount_pp,
            }
          }
        }
        return dates
      }
      return []
    }

    private updateAiport = (data: IPricesResult) => {
      const airportMap = new Map(this.props.funnelOrder.airports)
      let airports: Airport[] = airportMap.get(this.props.country) || []
      if (data.airport_bundles) {
        airports = data.airport_bundles.map(airportBundle =>
          mapAPIToAirport(airportBundle),
        )
      }

      this.props.addAirports(
        getVisibleAirports(airports, this.props.funnelOrder.destination),
      )
    }

    private updateFirstMonth = (data: IPricesResult) => {
      if (data.prices.length > 0) {
        const firstMonth = new Date(data.prices[0].date).getMonth() + 1
        const firstYear = new Date(data.prices[0].date).getFullYear()
        if (
          firstMonth !== this.props.funnelOrder.month ||
          firstYear !== this.props.funnelOrder.year
        ) {
          this.props.setMonthAndYear(firstMonth, firstYear)
          return new Date(firstYear, firstMonth - 1, 1)
        }
      }
      return undefined
    }

    // URL
    private getOptionsFromUrl() {
      return new Promise<void>((resolve, reject) => {
        // AIRPORTS
        if (queryKeys.includes(QueryKey.airports)) {
          const selectedAirports = this.query[QueryKey.airports]
          if (selectedAirports !== undefined) {
            const airports = selectedAirports.split(',')
            const airportIds = airports.map(x => Number(x))
            const currentAirportIds = this.state.order.airports.map(x =>
              Number(x),
            )
            const difference = airportIds
              .filter(x => !currentAirportIds.includes(x))
              .concat(currentAirportIds.filter(x => !airportIds.includes(x)))
            if (difference.length > 0) {
              const allowedAirports = this.state.airports.filter(
                airport => airportIds.indexOf(airport.id) !== -1,
              )
              if (allowedAirports.length > 0) {
                this.props.setAirports([])
                this.props.addAirports(allowedAirports)
              }
            }
          }
        }

        const partialOrder = getOptions(
          this.query,
          this.props.funnelOrder,
          queryKeys,
        )

        // cheapest
        if (this.query.cheapest) {
          partialOrder.departureDate = null
        }

        let hasMonth = this.state.hasMonth
        if (queryKeys.includes(QueryKey.month)) {
          if (this.query.month !== undefined) {
            partialOrder.month = Number(this.query.month)
            hasMonth = true
          }
        }
        if (queryKeys.includes(QueryKey.year)) {
          if (this.query.year !== undefined) {
            partialOrder.year = Number(this.query.year)
            hasMonth = true
          }
        }
        updateFunnelOrder(partialOrder)
        const order = Object.assign({}, this.state.order, partialOrder)
        this.setState({ order, hasMonth }, resolve)
      })
    }

    private setUrlOptions() {
      const originalQuery = getQueryParameters()
      const query = Object.assign({}, originalQuery)

      const autoQueryKeys = queryKeys.filter(x => x !== QueryKey.airports)
      autoQueryKeys.forEach((key: QueryKey) => {
        const order = this.props.funnelOrder as any
        if (
          order.hasOwnProperty(key) &&
          order[key] !== null &&
          originalQuery[key] !== order[key].toString()
        ) {
          query[key] = order[key].toString()
        }
      })

      if (queryKeys.includes(QueryKey.airports)) {
        const currentAirports: number[] = []
        for (const countryAirport of this.props.funnelOrder.airports) {
          for (const airport of countryAirport[1]) {
            currentAirports.push(airport.id)
          }
        }
        if (originalQuery[QueryKey.airports] !== currentAirports.join(',')) {
          query[QueryKey.airports] = currentAirports.join(',')
        }
      }

      if (JSON.stringify(query) !== JSON.stringify(originalQuery)) {
        history.replace(`${location.pathname}?${createQueryString(query)}`)
      }
    }
  }

  return connect(
    (state: AppState) => ({
      funnelOrder: state.funnelOrder,
      currency: state.locale.currency,
      country: state.locale.country,
      departureDate: state.funnelOrder.departureDate,
      theme: state.funnelOrder.theme,
    }),
    dispatch => ({
      setAirports: (airports: CountryAirports[]) => {
        dispatch(actions.change('funnelOrder.airports', airports))
      },
      setDay: (day: Date | null) => {
        dispatch(actions.change('funnelOrder.departureDate', day))
      },
      setFunnelOrder: (value: FunnelOrder) => {
        dispatch(actions.change('funnelOrder', value))
      },
      setMonthAndYear: (month: number, year: number) => {
        dispatch(actions.change('funnelOrder.month', month))
        dispatch(actions.change('funnelOrder.year', year))
      },
    }),
    (stateProps, dispatchProps, ownProps) => ({
      ...stateProps,
      ...ownProps,
      ...dispatchProps,
      addAirports: (airports: Airport[]) => {
        const airportMap = new Map(stateProps.funnelOrder.airports)
        airportMap.set(stateProps.country, airports)
        dispatchProps.setAirports(Array.from(airportMap.entries()))
      },
    }),
  )(Wrapper as any)
}
